import { createSelector, createSlice } from "@reduxjs/toolkit";
import { convert } from "../../components/Math/Conversion/convert.js";
import { ImperialUnits } from "../../components/Math/Units/AllUnits.jsx";
import { CLAY_OR_ROCK, COMPRESSION_PILE } from "../../constants.js";
import { reset } from "../resetParameters.js";
import { selectIsImperial, selectIsMetric, selectMeasurementType } from "./settingsSlice.js";

const initialState = {
  loadType: COMPRESSION_PILE,
  totalDesignLoad: {
    value: 0,
    unit: ImperialUnits.Kilopound,
  },
  designCombinationName: "",
  deadLoadFactor: 0,
  liveLoadFactor: 0,
  liveLoad: {
    value: 0,
    unit: ImperialUnits.KiloNewton,
  },
  deadLoad: {
    value: 0,
    unit: ImperialUnits.KiloNewton,
  },
  twentyEightDayGroutStrength: {
    value: 5,
    unit: ImperialUnits.KilopoundPerSquareInch,
  },
  holeSize: {
    value: 0,
    unit: ImperialUnits.Inch,
  },
  holeSizeIsCustom: false,
  factoredCompressionResistance: {
    value: 0,
    unit: ImperialUnits.Kilopound,
  },
  factoredTensionResistance: {
    value: 0,
    unit: ImperialUnits.Kilopound,
  },
  selectedCarryingElement: {
    name: "TITAN 30/11",
  },
  frontendState: {
    calculatedDesignLoadCheckbox: false,
    showBarDataDetails: false,
  },
  extensionIntoFoundation: { value: 0, unit: ImperialUnits.Foot },
  layersInput: [
    {
      index: 0,
      description: "Layer 1",
      widening: { value: CLAY_OR_ROCK, unit: ImperialUnits.Inch },
      layerTop: { value: 0, unit: ImperialUnits.Foot },
      layerBottom: { value: Number.MAX_SAFE_INTEGER, unit: ImperialUnits.Foot },
      skinFriction: { value: 0, unit: ImperialUnits.KilopoundPerSquaredFoot },
    },
  ],
  pileInclinationFromVertical: 0,
  barOD: { value: 0, unit: ImperialUnits.Inch },
  areaOfSteel: { value: 0, unit: ImperialUnits.InchSquared },
  fy: { value: 0, unit: ImperialUnits.KilopoundPerSquareInch },
  py: { value: 0, unit: ImperialUnits.Kilopound },
  fyControlling: { value: 0, unit: ImperialUnits.KilopoundPerSquareInch },
};

const aashtoSlice = createSlice({
  name: "aashto",
  initialState,
  reducers: {
    setAashtoState(state, action) {
      state.loadType = action.payload.loadType;
      state.totalDesignLoad = action.payload.totalDesignLoad;
      state.designCombinationName = action.payload.designCombinationName;
      state.deadLoadFactor = action.payload.deadLoadFactor;
      state.liveLoadFactor = action.payload.liveLoadFactor;
      state.liveLoad = action.payload.liveLoad;
      state.deadLoad = action.payload.deadLoad;
      state.twentyEightDayGroutStrength = action.payload.twentyEightDayGroutStrength;
      state.holeSize = action.payload.holeSize;
      state.holeSizeIsCustom = action.payload.holeSizeIsCustom;
      state.factoredCompressionResistance = action.payload.factoredCompressionResistance;
      state.factoredTensionResistance = action.payload.factoredTensionResistance;
      state.selectedCarryingElement = action.payload.selectedCarryingElement;
      state.frontendState = action.payload.frontendState;
      state.extensionIntoFoundation = action.payload.extensionIntoFoundation;
      state.layersInput = action.payload.layersInput;
      state.pileInclinationFromVertical = action.payload.pileInclinationFromVertical;
      state.barOD = action.payload.barOD;
      state.areaOfSteel = action.payload.areaOfSteel;
      state.fy = action.payload.fy;
      state.py = action.payload.py;
      state.fyControlling = action.payload.fyControlling;
    },
    setBarOD(state, action) {
      state.barOD = action.payload;
    },
    setAreaOfSteel(state, action) {
      state.areaOfSteel = action.payload;
    },
    setFy(state, action) {
      state.fy = action.payload;
    },
    setPy(state, action) {
      state.py = action.payload;
    },
    setFyControlling(state, action) {
      state.fyControlling = action.payload;
    },
    setFrontendState(state, action) {
      state.frontendState = {
        ...state.frontendState,
        ...action.payload,
      };
    },

    addLayer(state, action) {
      state.layersInput.push(action.payload);
    },
    deleteLayer(state, action) {
      const { position } = action.payload;
      state.layersInput.splice(position, 1);
    },
    updateLayerValue(state, action) {
      const { index } = action.payload;
      state.layersInput[index] = {
        ...state.layersInput[index],
        ...action.payload,
      };
    },
    setExtensionIntoFoundation(state, action) {
      state.extensionIntoFoundation = action.payload;
    },
    setLoadType(state, action) {
      state.loadType = action.payload;
    },
    setCarryingElementName(state, action) {
      state.selectedCarryingElement.name = action.payload;
    },
    setTwentyEightDayGroutStrength(state, action) {
      state.twentyEightDayGroutStrength = action.payload;
    },
    setFactoredCompressionResistance(state, action) {
      state.factoredCompressionResistance = action.payload;
    },
    setDesignCombinationName(state, action) {
      state.designCombinationName = action.payload;
    },
    setDesignCombinationFactors(state, action) {
      state.deadLoadFactor = action.payload.deadLoadFactor;
      state.liveLoadFactor = action.payload.liveLoadFactor;
    },
    setDesignCombinationDeadLoad(state, action) {
      state.deadLoad = action.payload;
    },
    setDesignCombinationLiveLoad(state, action) {
      state.liveLoad = action.payload;
    },
    setTotalDesignLoad(state, action) {
      state.totalDesignLoad = action.payload;
    },
    setCalculateDesignLoadCheckbox(state, action) {
      state.frontendState.calculatedDesignLoadCheckbox = action.payload;
    },
    setHoleSize(state, action) {
      state.holeSize = action.payload;
      state.holeSizeIsCustom = false;
    },
    setHoleSizeCustom(state, action) {
      state.holeSize = action.payload;
      state.holeSizeIsCustom = true;
    },
    setFactoredTensionResistance(state, action) {
      state.factoredTensionResistance = action.payload;
    },
    setPileInclinationFromVertical(state, action) {
      state.pileInclinationFromVertical = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(reset, (state, action) => {
      return initialState;
    });
  },
});

export const {
  setAashtoState,
  setBarOD,
  setAreaOfSteel,
  setFy,
  setPy,
  setFyControlling,
  setFrontendState,
  setExtensionIntoFoundation,
  setLoadType,
  setCarryingElementName,
  setTwentyEightDayGroutStrength,
  setFactoredCompressionResistance,
  setDesignCombinationName,
  setDesignCombinationFactors,
  setDesignCombinationDeadLoad,
  setDesignCombinationLiveLoad,
  setTotalDesignLoad,
  setCalculateDesignLoadCheckbox,
  setHoleSize,
  setHoleSizeCustom,
  setFactoredTensionResistance,
  setPileInclinationFromVertical,
  addLayer,
  deleteLayer,
  updateLayerValue,
} = aashtoSlice.actions;

export default aashtoSlice.reducer;

export const selectLayers = (state) => state.aashto.layersInput;
export const selectLoadType = (state) => state.aashto.loadType;
export const selectDesignCombinationName = (state) => state.aashto.designCombinationName;
export const selectDeadLoadFactor = (state) => state.aashto.deadLoadFactor;
export const selectLiveLoadFactor = (state) => state.aashto.liveLoadFactor;
export const selectedCarryingElement = (state) => state.aashto.selectedCarryingElement;
export const selectPileInclinationFromVertical = (state) =>
  state.aashto.pileInclinationFromVertical;

export const selectAllInputLayers = createSelector(
  [selectIsImperial, selectIsMetric, selectLayers],
  (isImperial, isMetric, layersInput) => {
    let arr = [];
    layersInput.forEach((layer) => {
      const { index, widening, layerTop, layerBottom, skinFriction, ...rest } = layer;
      arr.push({
        index,
        widening: convert(widening, isImperial, isMetric),
        layerTop: convert(layerTop, isImperial, isMetric),
        layerBottom: convert(layerBottom, isImperial, isMetric),
        skinFriction: convert(skinFriction, isImperial, isMetric),
        ...rest,
      });
    });
    return arr;
  }
);

export const selectHoleSize = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.holeSize],
  (isImperial, isMetric, holeSize) => {
    return convert(holeSize, isImperial, isMetric);
  }
);

export const selectLayersCalculated = createSelector(
  [
    selectIsImperial,
    selectIsMetric,
    selectAllInputLayers,
    selectPileInclinationFromVertical,
    selectHoleSize,
    (state) => state.aashto.totalDesignLoad,
  ],
  (isImperial, isMetric, layersInput, pileInclinationFromVertical, holeSize, totalDesignLoad) => {
    const totalDL = convert(totalDesignLoad, isImperial, isMetric);

    function degreeToRadians(degree) {
      return (Number(degree) * Math.PI) / 180;
    }

    function calcInclinedLength(from, to, angle) {
      let ilength =
        Number(to.imperial) === Number.MAX_SAFE_INTEGER
          ? Number.MAX_SAFE_INTEGER
          : Math.abs(Number(to.imperial) - Number(from.imperial)) /
            Math.cos(degreeToRadians(Number(angle)));

      return {
        value: ilength,
        unit: ImperialUnits.Foot,
      };
    }

    function calcLoadTaken(holeSize, widening, skinfriction, inclinedLength) {
      let loadTaken =
        Number(inclinedLength.imperial) === Number.MAX_SAFE_INTEGER
          ? Number(totalDL.imperial)
          : Math.PI *
            (((Number(holeSize.imperial) + Number(widening.imperial)) / 12) *
              Number(inclinedLength.imperial) *
              Number(skinfriction.imperial));

      return {
        value: loadTaken,
        unit: ImperialUnits.Kilopound,
      };
    }

    let arr = [];
    let iLSum = 0;
    layersInput.forEach((el, index) => {
      let inclinedLengthCalc = calcInclinedLength(
        el.layerTop,
        el.layerBottom,
        pileInclinationFromVertical
      );
      let inclinedLength = convert(inclinedLengthCalc, isImperial, isMetric);

      let loadTakenInLayerCalc = calcLoadTaken(
        holeSize,
        el.widening,
        el.skinFriction,
        inclinedLength
      );
      let diff =
        index > 0 && Number(inclinedLength.imperial) === Number.MAX_SAFE_INTEGER
          ? {
              value: Number(totalDL.imperial) - iLSum,
              unit: "Kilopound",
            }
          : loadTakenInLayerCalc;

      let loadTakenInLayer = convert(diff, isImperial, isMetric);

      iLSum += loadTakenInLayerCalc.value;
      arr.push({
        index,
        inclinedLength,
        loadTakenInLayer,
      });
    });
    return arr;
  }
);

export const selectSumLoadTakenInLayerTotal = createSelector(
  [selectIsImperial, selectIsMetric, selectLayersCalculated],
  (isImperial, isMetric, layersCalculated) => {
    let result = layersCalculated.reduce(function (total, layer) {
      return Number(total) + Number(layer.loadTakenInLayer.imperial);
    }, 0);

    var SumLoadTakenInLayer = {
      value: result,
      unit: ImperialUnits.Kilopound,
    };

    return convert(SumLoadTakenInLayer, isImperial, isMetric);
  }
);

export const selectTotalDesignLoad = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.totalDesignLoad],
  (isImperial, isMetric, totalDesignLoad) => {
    return convert(totalDesignLoad, isImperial, isMetric);
  }
);

export const selectTableUtilization = createSelector(
  [selectSumLoadTakenInLayerTotal, selectTotalDesignLoad],
  (sumLoadTakenInLayerTotal, totalDesignLoad) => {
    if (sumLoadTakenInLayerTotal.current === 0) return 0;
    return Number(totalDesignLoad.current) / Number(sumLoadTakenInLayerTotal.current);
  }
);

export const selectAg = createSelector(
  [selectIsImperial, selectIsMetric, selectHoleSize],
  (isImperial, isMetric, bitholeSize) => {
    let result = (Math.PI * Math.pow(Number(bitholeSize.imperial), 2)) / 4;

    let ag = { value: result, unit: ImperialUnits.InchSquared };
    return convert(ag, isImperial, isMetric);
  }
);
export const selectBarOD = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.barOD],
  (isImperial, isMetric, barOD) => {
    return convert(barOD, isImperial, isMetric);
  }
);

export const selectAreaOfSteel = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.areaOfSteel],
  (isImperial, isMetric, areaOfSteel) => {
    return convert(areaOfSteel, isImperial, isMetric);
  }
);

export const selectFy = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.fy],
  (isImperial, isMetric, fy) => {
    return convert(fy, isImperial, isMetric);
  }
);

export const selectPy = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.py],
  (isImperial, isMetric, py) => {
    return convert(py, isImperial, isMetric);
  }
);

export const selectFyControlling = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.fyControlling],
  (isImperial, isMetric, fyControlling) => {
    return convert(fyControlling, isImperial, isMetric);
  }
);

export const selectInclinedLengthSum = createSelector(
  [selectIsImperial, selectIsMetric, selectLayersCalculated, selectHoleSize, selectAllInputLayers],
  (isImperial, isMetric, layersCalculated, holeSize, inputLayers) => {
    let result = layersCalculated.reduce(function (total, layer) {
      return layer.inclinedLength.imperial === Number.MAX_SAFE_INTEGER
        ? Number(total) +
            Number(layer.loadTakenInLayer.imperial) /
              (Math.PI *
                (((Number(holeSize.imperial) + Number(inputLayers[layer.index].widening.imperial)) /
                  12) *
                  Number(inputLayers[layer.index].skinFriction.imperial)))
        : Number(total) + Number(layer.inclinedLength.imperial);
    }, 0);

    var InclinedLengthSum = {
      value: result,
      unit: ImperialUnits.Foot,
    };

    return convert(InclinedLengthSum, isImperial, isMetric);
  }
);

export const selectExtensionIntoFoundation = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.extensionIntoFoundation],
  (isImperial, isMetric, extensionIntoFoundation) => {
    return convert(extensionIntoFoundation, isImperial, isMetric);
  }
);

export const selectTotalLength = createSelector(
  [selectIsImperial, selectIsMetric, selectExtensionIntoFoundation, selectInclinedLengthSum],
  (isImperial, isMetric, extensionIntoFoundation, inclinedLengthSum) => {
    let result = Number(extensionIntoFoundation.imperial) + Number(inclinedLengthSum.imperial);

    var totalLength = {
      value: result,
      unit: ImperialUnits.Foot,
    };

    return convert(totalLength, isImperial, isMetric);
  }
);

export const selectDesignCombinationLiveLoad = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.liveLoad],
  (isImperial, isMetric, liveLoad) => {
    return convert(liveLoad, isImperial, isMetric);
  }
);

export const selectDesignCombinationDeadLoad = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.deadLoad],
  (isImperial, isMetric, deadLoad) => {
    return convert(deadLoad, isImperial, isMetric);
  }
);

export const selectTwentyEightDayGroutStrength = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.twentyEightDayGroutStrength],
  (isImperial, isMetric, twentyEightDayGroutStrength) => {
    // HACK: custom metric value
    if (
      isMetric &&
      twentyEightDayGroutStrength.value == 5 &&
      twentyEightDayGroutStrength.unit === ImperialUnits.KilopoundPerSquareInch
    ) {
      let result = {
        current: 35,
        imperial: 5,
        metric: 35,
      };
      return result;
    }

    return convert(twentyEightDayGroutStrength, isImperial, isMetric);
  }
);

export const selectFactoredCompressionResistance = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.factoredCompressionResistance],
  (isImperial, isMetric, factoredCompressionResistance) => {
    return convert(factoredCompressionResistance, isImperial, isMetric);
  }
);

export const selectFactoredTensionResistance = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.aashto.factoredTensionResistance],
  (isImperial, isMetric, factoredTensionResistance) => {
    return convert(factoredTensionResistance, isImperial, isMetric);
  }
);

export const selectResistanceFactor = createSelector([selectLoadType], (loadType) => {
  return loadType === COMPRESSION_PILE ? 0.75 : 0.8;
});

export const selectRcu = createSelector(
  [selectFactoredCompressionResistance, selectResistanceFactor],
  (factoredCompressionResistance, resistanceFactor) => {
    return Number(factoredCompressionResistance.current) * Number(resistanceFactor);
  }
);

export const selectUtilization = createSelector(
  [selectLoadType, selectTotalDesignLoad, selectRcu, selectFactoredTensionResistance],
  (loadType, totalDesignLoad, rcu, factoredTensionResistance) => {
    if (loadType === COMPRESSION_PILE) {
      if (rcu === 0) return 0;
      return Number(totalDesignLoad.current) / Number(rcu);
    } else {
      if (factoredTensionResistance.current === 0) return 0;
      return Number(totalDesignLoad.current) / Number(factoredTensionResistance.current);
    }
  }
);

export const selectFactorOfSafety = createSelector([selectUtilization], (utilization) => {
  if (utilization === 0) return 0;
  return 1 / Number(utilization);
});

export const selectAashtoAll = createSelector(
  [
    (state) => state.aashto,
    selectMeasurementType,
    selectLoadType,
    selectTotalDesignLoad,
    selectDesignCombinationLiveLoad,
    selectDesignCombinationDeadLoad,
    selectTwentyEightDayGroutStrength,
    selectHoleSize,
    selectFactoredCompressionResistance,
    selectFactoredTensionResistance,
    selectResistanceFactor,
    selectUtilization,
    selectFactorOfSafety,
    selectExtensionIntoFoundation,
    selectAllInputLayers,
    selectLayersCalculated,
    selectRcu,
    selectPileInclinationFromVertical,
    selectBarOD,
    selectAreaOfSteel,
    selectFy,
    selectPy,
    selectFyControlling,
    selectAg,
    selectInclinedLengthSum,
    selectTotalLength,
    selectTableUtilization,
    selectSumLoadTakenInLayerTotal,
  ],
  (
    state,
    measurementType,
    loadType,
    totalDesignLoad,
    liveLoad,
    deadLoad,
    twentyEightDayGroutStrength,
    holeSize,
    factoredCompressionResistance,
    factoredTensionResistance,
    resistanceFactor,
    utilization,
    factorOfSafety,
    extensionIntoFoundation,
    allInputLayers,
    layersCalculated,
    rcu,
    pileInclinationFromVertical,
    barOD,
    areaOfSteel,
    fy,
    py,
    fyControlling,
    ag,
    inclinedLengthSum,
    totalLength,
    tableUtilization,
    sumLoadTakenInLayerTotal
  ) => {
    // only use state if there is no specific select
    return {
      measurementType: measurementType,
      loadType: loadType,
      totalDesignLoad: totalDesignLoad,
      designCombinationName: state.designCombinationName,
      deadLoadFactor: state.deadLoadFactor,
      liveLoadFactor: state.liveLoadFactor,
      liveLoad: liveLoad,
      deadLoad: deadLoad,
      twentyEightDayGroutStrength: twentyEightDayGroutStrength,
      holeSize: holeSize,
      holeSizeIsCustom: state.holeSizeIsCustom,
      factoredCompressionResistance: factoredCompressionResistance,
      factoredTensionResistance: factoredTensionResistance,
      selectedCarryingElement: state.selectedCarryingElement,
      frontendState: state.frontendState,
      resistanceFactor: resistanceFactor,
      utilization: utilization,
      factorOfSafety: factorOfSafety,
      extensionIntoFoundation: extensionIntoFoundation,
      layersInput: allInputLayers,
      layersCalculated: layersCalculated,
      rcu: rcu,
      pileInclinationFromVertical: pileInclinationFromVertical,
      barOD: barOD,
      areaOfSteel: areaOfSteel,
      fy: fy,
      py: py,
      fyControlling: fyControlling,
      ag: ag,
      inclinedLengthTableSum: inclinedLengthSum,
      totalLength: totalLength,
      tableUtilization: tableUtilization,
      tableTotal: sumLoadTakenInLayerTotal,
    };
  }
);
